import { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { generatePath } from 'react-router';
import styled from 'styled-components';

import { useTypedParams } from '@sorare/routing';
import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { SearchInput } from '@sorare/core/src/atoms/inputs/SearchInput';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { Breadcrumb } from '@sorare/core/src/components/breadcrumb';
import {
  BLOG,
  BLOG_SPORT,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSeoContext } from '@sorare/core/src/contexts/seo';
import { glossary } from '@sorare/core/src/lib/glossary';
import { sportToBlogSportMap } from '@sorare/core/src/lib/routing';

import { BlogResults } from '#src/components/BlogResults/index.tsx';
import {
  FootballCategories,
  MlbNbaCategories,
} from '#src/components/Tabs/index.tsx';
import { useBlogSearch } from '#src/hooks/useBlogSearch.ts';

import type { PageParams } from './__generated__/routeParams.ts';

const Root = styled(Vertical).attrs({
  gap: 5,
})`
  margin-top: var(--triple-unit);
`;

const SearchWrapper = styled.div`
  max-width: fit-content;
`;

const messages = defineMessages({
  seoTitle: {
    defaultMessage: 'Fantasy Sports Reimagined - Sorare Blog',
  },
  seoDescription: {
    defaultMessage:
      'Experience football, basketball, and baseball like never before. Collect. Play. Win. #OwnYourGame.',
  },
});

export { loader } from '#src/components/BlogResults/index.tsx';

const HomeBySport = () => {
  const { setPageMetadata } = useSeoContext();
  const { formatMessage } = useIntl();
  const { sport } = useTypedParams<PageParams>();
  const sportCategory = sportToBlogSportMap[sport as Sport];

  useEffect(() => {
    setPageMetadata(formatMessage(messages.seoTitle), {
      description: formatMessage(messages.seoDescription),
    });
  }, [formatMessage, setPageMetadata]);

  const { searchInputValue, setSearchInputValue, isPending } = useBlogSearch();

  return (
    <Root gap={4}>
      <Breadcrumb
        sections={[
          { name: 'Blog', to: BLOG },
          {
            name: sportCategory ?? '',
            to: generatePath(BLOG_SPORT, {
              sport: sportCategory ?? '',
            }),
          },
        ]}
      />
      <TabBar variant="primary">
        {sportCategory === 'football' ? (
          <FootballCategories sportCategory={sportCategory} />
        ) : (
          <MlbNbaCategories sportCategory={sportCategory ?? 'nba'} />
        )}
      </TabBar>
      <SearchWrapper>
        <SearchInput
          withIcon
          placeholder={formatMessage(glossary.search)}
          value={searchInputValue}
          onChange={event => {
            setSearchInputValue(event.target.value);
          }}
        />
      </SearchWrapper>
      <BlogResults isSearching={isPending} />
    </Root>
  );
};
export default HomeBySport;
